/** Physics module. Use PhysicsHandler. Public: createWorld, createBody, syncMesh, step, createConstraint, createGroup, etc. */

import * as THREE from "three";
import * as CANNON from "cannon-es";
import {
  PHYSICS_CONSTANTS,
  PHYSICS_BODY_TYPES,
  type CreateWorldOptions,
  type CreateBodyOptions,
  type CreateBoxShapeOptions,
  type CreateSphereShapeOptions,
  type CreatePlaneShapeOptions,
  type CreateCylinderShapeOptions,
  type SyncMeshOptions,
  type ApplyVelocityOptions,
  type ApplyQuaternionOptions,
  type DistanceConstraintOptions,
  type HingeConstraintOptions,
  type PointToPointConstraintOptions,
  type SpringConstraintOptions,
  type CreateGroupOptions,
  type PhysicsGroupWithMeta,
  type PhysicsWorldWithMeta,
} from "./PhysicsHandlerTypes";

// Re-export types for convenience
export type {
  CreateWorldOptions,
  CreateBodyOptions,
  CreateBoxShapeOptions,
  CreateSphereShapeOptions,
  CreatePlaneShapeOptions,
  CreateCylinderShapeOptions,
  SyncMeshOptions,
  ApplyVelocityOptions,
  ApplyQuaternionOptions,
  DistanceConstraintOptions,
  HingeConstraintOptions,
  PointToPointConstraintOptions,
  SpringConstraintOptions,
  ConstraintOptions,
  CreateGroupOptions,
  PhysicsBodyWithMeta,
  PhysicsConstraintWithMeta,
  PhysicsGroupWithMeta,
  PhysicsWorldWithMeta,
} from "./PhysicsHandlerTypes";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function toVec3(
  v?: THREE.Vector3 | [number, number, number] | CANNON.Vec3
): CANNON.Vec3 {
  if (!v) return new CANNON.Vec3(0, 0, 0);
  if (v instanceof CANNON.Vec3) return v;
  if (v instanceof THREE.Vector3) return new CANNON.Vec3(v.x, v.y, v.z);
  if (Array.isArray(v)) return new CANNON.Vec3(v[0], v[1], v[2]);
  return new CANNON.Vec3(0, 0, 0);
}

function toQuat(
  q?:
    | THREE.Quaternion
    | THREE.Euler
    | [number, number, number, number]
    | [number, number, number]
    | CANNON.Quaternion
): CANNON.Quaternion {
  if (!q) return new CANNON.Quaternion();
  if (q instanceof CANNON.Quaternion) return q;
  if (q instanceof THREE.Quaternion) {
    return new CANNON.Quaternion(q.x, q.y, q.z, q.w);
  }
  if (q instanceof THREE.Euler) {
    const quat = new THREE.Quaternion().setFromEuler(q);
    return new CANNON.Quaternion(quat.x, quat.y, quat.z, quat.w);
  }
  if (Array.isArray(q)) {
    if (q.length === 4) {
      return new CANNON.Quaternion(q[0], q[1], q[2], q[3]);
    }
    if (q.length === 3) {
      const euler = new THREE.Euler(q[0], q[1], q[2]);
      const quat = new THREE.Quaternion().setFromEuler(euler);
      return new CANNON.Quaternion(quat.x, quat.y, quat.z, quat.w);
    }
  }
  return new CANNON.Quaternion();
}

function vec3ToThree(v: CANNON.Vec3): THREE.Vector3 {
  return new THREE.Vector3(v.x, v.y, v.z);
}

function quatToThree(q: CANNON.Quaternion): THREE.Quaternion {
  return new THREE.Quaternion(q.x, q.y, q.z, q.w);
}

// ============================================================================
// PHYSICS HANDLER CLASS
// ============================================================================

/**
 * Physics handler class for managing physics worlds, bodies, constraints, and joints.
 * Provides utilities for creation, deletion, fetching, pooling, grouping, and syncing.
 * 
 * @example
 * ```typescript
 * // Create an instance
 * const physicsHandler = new PhysicsHandler();
 * 
 * // Create a world
 * const world = physicsHandler.createWorld({
 *   gravity: [0, -9.82, 0],
 *   worldId: 'main-world'
 * });
 * 
 * // Create bodies
 * const body = physicsHandler.createBoxBody('main-world', {
 *   halfExtents: [0.5, 0.5, 0.5],
 *   position: [0, 5, 0]
 * });
 * ```
 */
export class PhysicsHandler {
  // Instance state
  private worldRegistry: Map<string, PhysicsWorldWithMeta>;
  private bodyPool: CANNON.Body[];
  private readonly bodyPoolMaxSize: number;

  /**
   * Create a new PhysicsHandler instance.
   */
  constructor() {
    this.worldRegistry = new Map<string, PhysicsWorldWithMeta>();
    this.bodyPool = [];
    this.bodyPoolMaxSize = 100;
  }

  // ============================================================================
  // WORLD MANAGEMENT
  // ============================================================================

  /**
   * Create a new physics world with sensible defaults.
   *
   * Why: Centralize world setup and configuration. Use this at game initialization
   * to create your main physics world or multiple worlds for different gameplay layers.
   *
   * When to use:
   * - Create the main physics world at game startup
   * - Create separate worlds for different game layers (UI physics, game physics, etc.)
   * - When you need custom gravity, solver settings, or world-specific configurations
   *
   * @param options - Configuration options for the physics world
   * @returns The created CANNON.World instance
   */
  createWorld(options: CreateWorldOptions = {}): CANNON.World {
    const world = new CANNON.World();

    // Set gravity
    const gravity = options.gravity || PHYSICS_CONSTANTS.DEFAULT_GRAVITY;
    world.gravity.set(gravity[0], gravity[1], gravity[2]);

    // Configure solver (cast to any for properties that may not be in types)
    if (options.solverIterations !== undefined) {
      (world.solver as any).iterations = options.solverIterations;
    }
    if (options.solverTolerance !== undefined) {
      (world.solver as any).tolerance = options.solverTolerance;
    }

    // Configure broadphase
    if (options.enableBroadphase !== false) {
      world.broadphase = new CANNON.NaiveBroadphase();
    }

    // Configure sleeping
    if (options.enableSleeping !== false) {
      world.allowSleep = true;
    }

    // Register world
    const worldId = options.worldId || `world_${Date.now()}_${Math.random()}`;
    const worldMeta: PhysicsWorldWithMeta = {
      world,
      worldId,
      bodies: new Map(),
      constraints: new Map(),
      groups: new Map(),
      bodyPool: [],
      isRunning: false,
    };
    this.worldRegistry.set(worldId, worldMeta);

    return world;
  }

  /**
   * Delete a physics world and all its bodies/constraints.
   */
  deleteWorld(worldId: string, options?: { returnToPool?: boolean }): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const { world, bodies, constraints } = worldMeta;

    // Return bodies to global pool if requested
    if (options?.returnToPool) {
      bodies.forEach((body) => {
        if (this.bodyPool.length < this.bodyPoolMaxSize) {
          this.resetBody(body);
          this.bodyPool.push(body);
        }
      });
    } else {
      // Remove all bodies
      bodies.forEach((body) => {
        world.removeBody(body);
        // Remove all shapes from body
        body.shapes.slice().forEach((shape: CANNON.Shape) => {
          body.removeShape(shape);
        });
      });
    }

    // Remove all constraints
    constraints.forEach((constraint) => {
      if (constraint instanceof CANNON.Spring) {
        // Springs are not constraints, they need manual application
        // Just remove from registry
      } else {
        world.removeConstraint(constraint as CANNON.Constraint);
      }
    });

    // Clear groups
    worldMeta.groups.clear();

    // Clean up world
    world.bodies = [];
    world.constraints = [];

    // Remove from registry
    this.worldRegistry.delete(worldId);

    return true;
  }

  /**
   * Get a physics world by ID.
   */
  getWorld(worldId: string): CANNON.World | null {
    const worldMeta = this.worldRegistry.get(worldId);
    return worldMeta?.world || null;
  }

  /**
   * Get world metadata by ID.
   */
  getWorldMeta(worldId: string): PhysicsWorldWithMeta | null {
    return this.worldRegistry.get(worldId) || null;
  }

  /**
   * Get all registered world IDs.
   */
  getAllWorldIds(): string[] {
    return Array.from(this.worldRegistry.keys());
  }

  /**
   * Start physics simulation for a world.
   */
  startWorld(worldId: string): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;
    worldMeta.isRunning = true;
    return true;
  }

  /**
   * Stop physics simulation for a world.
   */
  stopWorld(worldId: string): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;
    worldMeta.isRunning = false;
    return true;
  }

  /**
   * Step physics simulation for a world.
   */
  stepWorld(
    worldId: string,
    deltaTime: number,
    maxSubSteps?: number,
    fixedTimeStep?: number
  ): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta || !worldMeta.isRunning) return false;

    const maxSub = maxSubSteps || PHYSICS_CONSTANTS.MAX_SUBSTEPS;
    const fixedStep = fixedTimeStep || PHYSICS_CONSTANTS.DEFAULT_TIMESTEP;

    // Apply spring forces before stepping
    this.applySpringForces(worldId);

    // Step the physics world
    worldMeta.world.step(fixedStep, deltaTime, maxSub);
    return true;
  }

  // ============================================================================
  // BODY MANAGEMENT
  // ============================================================================

  /**
   * Create a physics body from a pool or new instance.
   */
  createBody(
    worldId: string,
    shape: CANNON.Shape,
    options: CreateBodyOptions = {}
  ): CANNON.Body {
    // Get world
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) {
      throw new Error(`World ${worldId} not found`);
    }

    // Try to get body from pool
    let body: CANNON.Body;
    if (this.bodyPool.length > 0) {
      body = this.bodyPool.pop()!;
      this.resetBody(body);
    } else {
      body = new CANNON.Body();
    }

    // Set body type
    body.type =
      options.type !== undefined ? options.type : PHYSICS_BODY_TYPES.DYNAMIC;

    // Set mass
    if (options.mass !== undefined) {
      body.mass = options.mass;
      body.updateMassProperties();
    } else if (body.type === PHYSICS_BODY_TYPES.STATIC) {
      body.mass = 0;
    } else {
      body.mass = PHYSICS_CONSTANTS.DEFAULT_MASS;
      body.updateMassProperties();
    }

    // Add shape
    body.addShape(shape);

    // Set position
    if (options.position) {
      const pos = toVec3(options.position);
      body.position.copy(pos);
    }

    // Set rotation
    if (options.rotation) {
      const rot = toQuat(options.rotation);
      body.quaternion.copy(rot);
    }

    // Set damping
    body.linearDamping =
      options.linearDamping !== undefined
        ? options.linearDamping
        : PHYSICS_CONSTANTS.DEFAULT_LINEAR_DAMPING;
    body.angularDamping =
      options.angularDamping !== undefined
        ? options.angularDamping
        : PHYSICS_CONSTANTS.DEFAULT_ANGULAR_DAMPING;

    // Set material properties
    body.material = new CANNON.Material();
    body.material.friction =
      options.friction !== undefined
        ? options.friction
        : PHYSICS_CONSTANTS.DEFAULT_FRICTION;
    body.material.restitution =
      options.restitution !== undefined
        ? options.restitution
        : PHYSICS_CONSTANTS.DEFAULT_RESTITUTION;

    // Set material name if provided
    if (options.material) {
      body.material.name = options.material;
    }

    // Set sleeping
    if (options.allowSleep !== undefined) {
      body.allowSleep = options.allowSleep;
    }
    if (options.sleepSpeedLimit !== undefined) {
      body.sleepSpeedLimit = options.sleepSpeedLimit;
    }

    // Set collision filters
    if (options.collisionFilterGroup !== undefined) {
      body.collisionFilterGroup = options.collisionFilterGroup;
    }
    if (options.collisionFilterMask !== undefined) {
      body.collisionFilterMask = options.collisionFilterMask;
    }

    // Set user data
    if (options.userData) {
      Object.assign(body, options.userData);
    }

    // Add to world
    worldMeta.world.addBody(body);

    // Register body
    const bodyId = options.bodyId || `body_${Date.now()}_${Math.random()}`;
    worldMeta.bodies.set(bodyId, body);
    (body as any).__bodyId = bodyId;
    (body as any).__worldId = worldId;

    return body;
  }

  /**
   * Create a box body (rectangular prism).
   */
  createBoxBody(
    worldId: string,
    options: CreateBodyOptions & CreateBoxShapeOptions
  ): CANNON.Body {
    const halfExtents = toVec3(options.halfExtents);
    const shape = new CANNON.Box(halfExtents);
    return this.createBody(worldId, shape, options);
  }

  /**
   * Create a sphere body.
   */
  createSphereBody(
    worldId: string,
    options: CreateBodyOptions & CreateSphereShapeOptions
  ): CANNON.Body {
    const shape = new CANNON.Sphere(options.radius);
    return this.createBody(worldId, shape, options);
  }

  /**
   * Create a plane body.
   */
  createPlaneBody(
    worldId: string,
    options: CreateBodyOptions & CreatePlaneShapeOptions = {}
  ): CANNON.Body {
    const normal = toVec3(options.normal || [0, 1, 0]);
    const shape = new CANNON.Plane();
    const body = this.createBody(worldId, shape, {
      ...options,
      type: PHYSICS_BODY_TYPES.STATIC,
    });

    // Rotate body if normal is not [0,1,0]
    if (normal.x !== 0 || normal.y !== 1 || normal.z !== 0) {
      const targetNormal = new THREE.Vector3(normal.x, normal.y, normal.z);
      const up = new THREE.Vector3(0, 1, 0);
      const quaternion = new THREE.Quaternion().setFromUnitVectors(
        up,
        targetNormal
      );
      body.quaternion.set(
        quaternion.x,
        quaternion.y,
        quaternion.z,
        quaternion.w
      );
    }

    return body;
  }

  /**
   * Create a cylinder body.
   */
  createCylinderBody(
    worldId: string,
    options: CreateBodyOptions & CreateCylinderShapeOptions
  ): CANNON.Body {
    const shape = new CANNON.Cylinder(
      options.radiusTop,
      options.radiusBottom,
      options.height,
      options.numSegments || 8
    );
    return this.createBody(worldId, shape, options);
  }

  /**
   * Delete a physics body.
   */
  deleteBody(worldId: string, bodyId: string, returnToPool = false): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const body = worldMeta.bodies.get(bodyId);
    if (!body) return false;

    // Remove from world
    worldMeta.world.removeBody(body);

    // Remove from groups
    worldMeta.groups.forEach((group) => {
      group.bodies.delete(bodyId);
    });

    // Remove from registry
    worldMeta.bodies.delete(bodyId);

    // Return to pool or remove shapes
    if (returnToPool && this.bodyPool.length < this.bodyPoolMaxSize) {
      this.resetBody(body);
      this.bodyPool.push(body);
    } else {
      // Remove all shapes from body
      body.shapes.slice().forEach((shape: CANNON.Shape) => {
        body.removeShape(shape);
      });
    }

    delete (body as any).__bodyId;
    delete (body as any).__worldId;

    return true;
  }

  /**
   * Get a physics body by ID.
   */
  getBody(worldId: string, bodyId: string): CANNON.Body | null {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return null;
    return worldMeta.bodies.get(bodyId) || null;
  }

  /**
   * Get all body IDs in a world.
   */
  getBodyIds(worldId: string): string[] {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return [];
    return Array.from(worldMeta.bodies.keys());
  }

  /**
   * Reset a body to default state (for pooling).
   */
  private resetBody(body: CANNON.Body): void {
    // Remove all shapes
    body.shapes.slice().forEach((shape: CANNON.Shape) => {
      body.removeShape(shape);
    });

    // Reset properties
    body.position.set(0, 0, 0);
    body.quaternion.set(0, 0, 0, 1);
    body.velocity.set(0, 0, 0);
    body.angularVelocity.set(0, 0, 0);
    body.force.set(0, 0, 0);
    body.torque.set(0, 0, 0);
    body.mass = 1;
    body.material = new CANNON.Material();
    body.type = PHYSICS_BODY_TYPES.DYNAMIC;
    body.linearDamping = PHYSICS_CONSTANTS.DEFAULT_LINEAR_DAMPING;
    body.angularDamping = PHYSICS_CONSTANTS.DEFAULT_ANGULAR_DAMPING;
    body.allowSleep = true;
    body.updateMassProperties();

    // Clear user data
    delete (body as any).__bodyId;
    delete (body as any).__worldId;
    delete (body as any).__groupId;
    delete (body as any).__mesh;
  }

  // ============================================================================
  // GROUPING
  // ============================================================================

  /**
   * Create a physics group for organizing bodies.
   */
  createGroup(worldId: string, options: CreateGroupOptions = {}): string {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) {
      throw new Error(`World ${worldId} not found`);
    }

    const groupId = options.groupId || `group_${Date.now()}_${Math.random()}`;
    const group: PhysicsGroupWithMeta = {
      groupId,
      bodies: new Map(),
      worldId,
      collisionFilterGroup: options.collisionFilterGroup || 1,
      collisionFilterMask: options.collisionFilterMask || -1,
    };

    worldMeta.groups.set(groupId, group);
    return groupId;
  }

  /**
   * Add a body to a group.
   */
  addBodyToGroup(worldId: string, bodyId: string, groupId: string): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const group = worldMeta.groups.get(groupId);
    if (!group) return false;

    const body = worldMeta.bodies.get(bodyId);
    if (!body) return false;

    group.bodies.set(bodyId, body);
    body.collisionFilterGroup = group.collisionFilterGroup;
    body.collisionFilterMask = group.collisionFilterMask;
    (body as any).__groupId = groupId;

    return true;
  }

  /**
   * Remove a body from a group.
   */
  removeBodyFromGroup(
    worldId: string,
    bodyId: string,
    groupId: string
  ): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const group = worldMeta.groups.get(groupId);
    if (!group) return false;

    group.bodies.delete(bodyId);
    delete (worldMeta.bodies.get(bodyId) as any)?.__groupId;

    return true;
  }

  /**
   * Get a group by ID.
   */
  getGroup(worldId: string, groupId: string): PhysicsGroupWithMeta | null {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return null;
    return worldMeta.groups.get(groupId) || null;
  }

  /**
   * Delete a group (does not delete bodies, just removes group).
   */
  deleteGroup(worldId: string, groupId: string): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const group = worldMeta.groups.get(groupId);
    if (!group) return false;

    // Remove group references from bodies
    group.bodies.forEach((body) => {
      delete (body as any).__groupId;
    });

    worldMeta.groups.delete(groupId);
    return true;
  }

  /**
   * Get all group IDs in a world.
   */
  getGroupIds(worldId: string): string[] {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return [];
    return Array.from(worldMeta.groups.keys());
  }

  // ============================================================================
  // CONSTRAINTS AND JOINTS
  // ============================================================================

  /**
   * Create a distance constraint between two bodies.
   */
  createDistanceConstraint(
    worldId: string,
    bodyAId: string,
    bodyBId: string,
    options: DistanceConstraintOptions = {}
  ): CANNON.PointToPointConstraint {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) {
      throw new Error(`World ${worldId} not found`);
    }

    const bodyA = worldMeta.bodies.get(bodyAId);
    const bodyB = worldMeta.bodies.get(bodyBId);
    if (!bodyA || !bodyB) {
      throw new Error("Body not found");
    }

    const pivotA = toVec3(options.pivotA || [0, 0, 0]);
    const pivotB = toVec3(options.pivotB || [0, 0, 0]);

    const constraint = new CANNON.PointToPointConstraint(
      bodyA,
      pivotA,
      bodyB,
      pivotB
    );

    worldMeta.world.addConstraint(constraint);
    worldMeta.constraints.set(
      `constraint_${Date.now()}_${Math.random()}`,
      constraint
    );

    return constraint;
  }

  /**
   * Create a hinge constraint between two bodies.
   */
  createHingeConstraint(
    worldId: string,
    bodyAId: string,
    bodyBId: string,
    options: HingeConstraintOptions = {}
  ): CANNON.HingeConstraint {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) {
      throw new Error(`World ${worldId} not found`);
    }

    const bodyA = worldMeta.bodies.get(bodyAId);
    const bodyB = worldMeta.bodies.get(bodyBId);
    if (!bodyA || !bodyB) {
      throw new Error("Body not found");
    }

    const pivotA = toVec3(options.pivotA || [0, 0, 0]);
    const pivotB = toVec3(options.pivotB || [0, 0, 0]);
    const axisA = toVec3(options.axisA || [0, 0, 1]);
    const axisB = toVec3(options.axisB || [0, 0, 1]);

    const constraint = new CANNON.HingeConstraint(bodyA, bodyB, {
      pivotA,
      pivotB,
      axisA,
      axisB,
      maxForce: options.maxAngle !== undefined ? 1e6 : undefined,
    });

    worldMeta.world.addConstraint(constraint);
    worldMeta.constraints.set(
      `constraint_${Date.now()}_${Math.random()}`,
      constraint
    );

    return constraint;
  }

  /**
   * Create a point-to-point constraint.
   */
  createPointToPointConstraint(
    worldId: string,
    bodyAId: string,
    bodyBId: string,
    options: PointToPointConstraintOptions = {}
  ): CANNON.PointToPointConstraint {
    return this.createDistanceConstraint(worldId, bodyAId, bodyBId, options);
  }

  /**
   * Create a spring constraint between two bodies.
   */
  createSpringConstraint(
    worldId: string,
    bodyAId: string,
    bodyBId: string,
    options: SpringConstraintOptions = {}
  ): CANNON.Spring {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) {
      throw new Error(`World ${worldId} not found`);
    }

    const bodyA = worldMeta.bodies.get(bodyAId);
    const bodyB = worldMeta.bodies.get(bodyBId);
    if (!bodyA || !bodyB) {
      throw new Error("Body not found");
    }

    const localAnchorA = toVec3(options.localAnchorA || [0, 0, 0]);
    const localAnchorB = toVec3(options.localAnchorB || [0, 0, 0]);

    const spring = new CANNON.Spring(bodyA, bodyB, {
      localAnchorA,
      localAnchorB,
      restLength: options.restLength || 1,
      stiffness: options.stiffness || 100,
      damping: options.damping || 1,
    });

    // Springs are not added to world like constraints
    // They need to be applied manually in the update loop via spring.applyForce()
    // Store reference for manual application
    worldMeta.constraints.set(`spring_${Date.now()}_${Math.random()}`, spring);

    return spring;
  }

  /**
   * Delete a constraint.
   */
  deleteConstraint(worldId: string, constraintId: string): boolean {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return false;

    const constraint = worldMeta.constraints.get(constraintId);
    if (!constraint) return false;

    if (constraint instanceof CANNON.Spring) {
      // Springs are not constraints, just remove from registry
    } else {
      worldMeta.world.removeConstraint(constraint as CANNON.Constraint);
    }

    worldMeta.constraints.delete(constraintId);
    return true;
  }

  /**
   * Get all constraint IDs in a world.
   */
  getConstraintIds(worldId: string): string[] {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return [];
    return Array.from(worldMeta.constraints.keys());
  }

  // ============================================================================
  // MOVEMENT AND UTILITIES
  // ============================================================================

  /**
   * Apply velocity to a physics body.
   */
  applyVelocity(
    worldId: string,
    bodyId: string,
    options: ApplyVelocityOptions
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    if (options.linear) {
      const linear = toVec3(options.linear);
      if (options.add) {
        body.velocity.vadd(linear, body.velocity);
      } else {
        body.velocity.copy(linear);
      }
    }

    if (options.angular) {
      const angular = toVec3(options.angular);
      if (options.add) {
        body.angularVelocity.vadd(angular, body.angularVelocity);
      } else {
        body.angularVelocity.copy(angular);
      }
    }

    return true;
  }

  /**
   * Apply quaternion rotation to a physics body.
   */
  applyQuaternion(
    worldId: string,
    bodyId: string,
    options: ApplyQuaternionOptions
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    let quat: CANNON.Quaternion;

    if (options.quaternion) {
      quat = toQuat(options.quaternion);
    } else if (options.euler) {
      quat = toQuat(options.euler);
    } else {
      return false;
    }

    if (options.multiply) {
      body.quaternion = body.quaternion.mult(quat);
    } else {
      body.quaternion.copy(quat);
    }

    return true;
  }

  /**
   * Sync a Three.js mesh/group with a physics body.
   */
  syncMeshWithBody(
    worldId: string,
    bodyId: string,
    mesh: THREE.Mesh | THREE.Group | THREE.Object3D,
    options: SyncMeshOptions = {}
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const syncPosition = options.syncPosition !== false;
    const syncRotation = options.syncRotation !== false;
    const positionOffset = toVec3(options.positionOffset);
    const rotationOffset = options.rotationOffset
      ? toQuat(options.rotationOffset)
      : null;

    if (syncPosition) {
      const pos = vec3ToThree(body.position);
      if (
        positionOffset.x !== 0 ||
        positionOffset.y !== 0 ||
        positionOffset.z !== 0
      ) {
        pos.add(vec3ToThree(positionOffset));
      }
      mesh.position.copy(pos);
    }

    if (syncRotation) {
      let rot = quatToThree(body.quaternion);
      if (rotationOffset) {
        rot = rot.multiply(quatToThree(rotationOffset));
      }
      mesh.quaternion.copy(rot);
    }

    // Store reference for reverse sync if needed
    (body as any).__mesh = mesh;
    (mesh as any).__physicsBody = body;

    return true;
  }

  /**
   * Sync a physics body with a Three.js mesh/group (reverse sync).
   */
  syncBodyWithMesh(
    worldId: string,
    bodyId: string,
    mesh: THREE.Mesh | THREE.Group | THREE.Object3D,
    options: SyncMeshOptions = {}
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const syncPosition = options.syncPosition !== false;
    const syncRotation = options.syncRotation !== false;
    const positionOffset = toVec3(options.positionOffset);
    const rotationOffset = options.rotationOffset
      ? toQuat(options.rotationOffset)
      : null;

    if (syncPosition) {
      const pos = toVec3(mesh.position);
      if (
        positionOffset.x !== 0 ||
        positionOffset.y !== 0 ||
        positionOffset.z !== 0
      ) {
        pos.vsub(positionOffset, pos);
      }
      body.position.copy(pos);
    }

    if (syncRotation) {
      let quat = toQuat(mesh.quaternion);
      if (rotationOffset) {
        quat = quat.mult(rotationOffset);
      }
      body.quaternion.copy(quat);
    }

    // Wake up body if it was sleeping
    body.wakeUp();

    return true;
  }

  /**
   * Sync all bodies in a world with their meshes.
   */
  syncAllBodies(worldId: string, options: SyncMeshOptions = {}): void {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return;

    worldMeta.bodies.forEach((body, bodyId) => {
      const mesh = (body as any).__mesh;
      if (mesh) {
        this.syncMeshWithBody(worldId, bodyId, mesh, options);
      }
    });
  }

  /**
   * Apply all spring forces in a world.
   */
  applySpringForces(worldId: string): void {
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return;

    worldMeta.constraints.forEach((constraint) => {
      if (constraint instanceof CANNON.Spring) {
        constraint.applyForce();
      }
    });
  }

  /**
   * Apply force to a body.
   */
  applyForce(
    worldId: string,
    bodyId: string,
    force: [number, number, number] | THREE.Vector3 | CANNON.Vec3,
    point?: [number, number, number] | THREE.Vector3 | CANNON.Vec3
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const forceVec = toVec3(force);
    if (point) {
      const pointVec = toVec3(point);
      body.applyForce(forceVec, pointVec);
    } else {
      body.applyForce(forceVec);
    }

    return true;
  }

  /**
   * Apply impulse to a body.
   */
  applyImpulse(
    worldId: string,
    bodyId: string,
    impulse: [number, number, number] | THREE.Vector3 | CANNON.Vec3,
    point?: [number, number, number] | THREE.Vector3 | CANNON.Vec3
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const impulseVec = toVec3(impulse);
    if (point) {
      const pointVec = toVec3(point);
      body.applyImpulse(impulseVec, pointVec);
    } else {
      body.applyImpulse(impulseVec);
    }

    return true;
  }

  /**
   * Apply torque to a body.
   */
  applyTorque(
    worldId: string,
    bodyId: string,
    torque: [number, number, number] | THREE.Vector3 | CANNON.Vec3
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const torqueVec = toVec3(torque);
    body.applyTorque(torqueVec);

    return true;
  }

  /**
   * Set body position.
   */
  setBodyPosition(
    worldId: string,
    bodyId: string,
    position: [number, number, number] | THREE.Vector3
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const pos = toVec3(position);
    body.position.copy(pos);
    body.wakeUp();

    return true;
  }

  /**
   * Set body rotation.
   */
  setBodyRotation(
    worldId: string,
    bodyId: string,
    rotation:
      | [number, number, number, number]
      | [number, number, number]
      | THREE.Quaternion
      | THREE.Euler
  ): boolean {
    const body = this.getBody(worldId, bodyId);
    if (!body) return false;

    const rot = toQuat(rotation);
    body.quaternion.copy(rot);
    body.wakeUp();

    return true;
  }

  /**
   * Get body position as Three.js Vector3.
   */
  getBodyPosition(worldId: string, bodyId: string): THREE.Vector3 | null {
    const body = this.getBody(worldId, bodyId);
    if (!body) return null;
    return vec3ToThree(body.position);
  }

  /**
   * Get body rotation as Three.js Quaternion.
   */
  getBodyRotation(worldId: string, bodyId: string): THREE.Quaternion | null {
    const body = this.getBody(worldId, bodyId);
    if (!body) return null;
    return quatToThree(body.quaternion);
  }

  /**
   * Sync kinematic platform movement and apply to rider body
   * Handles the complete kinematic platform workflow:
   * 1. Syncs kinematic body with its mesh to get current position
   * 2. Calculates displacement from previous position
   * 3. Applies displacement to the rider body (e.g. player)
   * 4. Returns displacement for tracking
   * 
   * @param worldId - World identifier
   * @param kinematicBodyId - Body identifier of the kinematic platform
   * @param kinematicMesh - Three.js mesh of the kinematic platform
   * @param riderBodyId - Body identifier of the rider (e.g. player)
   * @param previousPosition - Previous position of the kinematic platform [x, y, z]
   * @returns Object with displacement [x, y, z] and success flag, or null if bodies not found
   * 
   * @example
   * ```typescript
   * // Store previous platform position each frame
   * const prevPos = [platform.mesh.position.x, platform.mesh.position.y, platform.mesh.position.z];
   * 
   * // Apply platform movement to player
   * if (player.isGrounded && onPlatform) {
   *   const result = physics.syncKinematicPlatformRider(
   *     'main',
   *     'platform-1',
   *     platformMesh,
   *     'player',
   *     prevPos
   *   );
   *   if (result) {
   *     console.log('Platform moved by:', result.displacement);
   *   }
   * }
   * ```
   */
  syncKinematicPlatformRider(
    worldId: string,
    kinematicBodyId: string,
    kinematicMesh: THREE.Object3D,
    riderBodyId: string,
    previousPosition: [number, number, number] | THREE.Vector3
  ): { displacement: [number, number, number]; applied: boolean } | null {
    const kinematicBody = this.getBody(worldId, kinematicBodyId);
    const riderBody = this.getBody(worldId, riderBodyId);

    if (!kinematicBody || !riderBody) return null;

    // Sync kinematic body with its mesh (get current position after movement)
    this.syncMeshWithBody(worldId, kinematicBodyId, kinematicMesh, {
      syncPosition: true,
      syncRotation: true,
    });

    // Calculate displacement from previous position
    const currentPos = kinematicMesh.position;
    const prevPos = Array.isArray(previousPosition)
      ? previousPosition
      : [previousPosition.x, previousPosition.y, previousPosition.z];

    const displacement: [number, number, number] = [
      currentPos.x - prevPos[0],
      currentPos.y - prevPos[1],
      currentPos.z - prevPos[2],
    ];

    // Apply displacement to rider body
    riderBody.position.x += displacement[0];
    riderBody.position.y += displacement[1];
    riderBody.position.z += displacement[2];

    riderBody.wakeUp();

    return {
      displacement,
      applied: true,
    };
  }

  /**
   * Check if a physics body is grounded using raycast
   * Casts a ray downward from the body's position to detect ground contact
   * 
   * @param worldId - World identifier
   * @param bodyId - Body identifier or CANNON.Body instance
   * @param rayDistance - Distance to cast ray downward (default: 1.0)
   * @param rayOriginOffset - Offset from body center to start ray (default: 0, useful for capsules/characters)
   * @returns Ground check result with hit info, or null if body/world not found
   * 
   * @example
   * ```typescript
   * // Check if player is grounded (1 unit ray)
   * const groundCheck = physics.checkGroundRaycast('main', 'player', 1.0);
   * if (groundCheck && groundCheck.isGrounded) {
   *   console.log('Player is on ground!');
   *   console.log('Distance to ground:', groundCheck.distance);
   * }
   * 
   * // For a capsule character (offset ray start from center)
   * const groundCheck = physics.checkGroundRaycast('main', 'player', 1.2, 0.5);
   * ```
   */
  checkGroundRaycast(
    worldId: string,
    bodyId: string | CANNON.Body,
    rayDistance: number = 1.0,
    rayOriginOffset: number = 0
  ): { isGrounded: boolean; distance: number; hitPoint: THREE.Vector3 | null; hitNormal: THREE.Vector3 | null } | null {
    // Get world
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return null;

    // Get body
    let body: CANNON.Body | null = null;
    if (typeof bodyId === 'string') {
      body = this.getBody(worldId, bodyId);
    } else {
      body = bodyId;
    }
    if (!body) return null;

    // Create ray from body position downward
    const from = new CANNON.Vec3(
      body.position.x,
      body.position.y - rayOriginOffset,
      body.position.z
    );
    const to = new CANNON.Vec3(
      body.position.x,
      body.position.y - rayOriginOffset - rayDistance,
      body.position.z
    );

    // Perform raycast (skip the body we're checking from to avoid self-collision)
    const result = new CANNON.RaycastResult();
    worldMeta.world.raycastClosest(from, to, {
      skipBackfaces: true,
      collisionFilterMask: -1,
    }, result);

    // Check if the hit body is the same as the body we're checking
    // If so, ignore this hit (it's hitting itself)
    if (result.hasHit && result.body === body) {
      return {
        isGrounded: false,
        distance: rayDistance,
        hitPoint: null,
        hitNormal: null,
      };
    }

    // Check if hit
    if (result.hasHit) {
      return {
        isGrounded: true,
        distance: from.distanceTo(result.hitPointWorld),
        hitPoint: new THREE.Vector3(
          result.hitPointWorld.x,
          result.hitPointWorld.y,
          result.hitPointWorld.z
        ),
        hitNormal: new THREE.Vector3(
          result.hitNormalWorld.x,
          result.hitNormalWorld.y,
          result.hitNormalWorld.z
        ),
      };
    }

    return {
      isGrounded: false,
      distance: rayDistance,
      hitPoint: null,
      hitNormal: null,
    };
  }

  /**
   * Check if a physics body is grounded using sphere cast
   * Casts a sphere downward from the body's position to detect ground contact
   * More robust than raycast - detects ground within sphere radius
   * 
   * @param worldId - World identifier
   * @param bodyId - Body identifier or CANNON.Body instance
   * @param sphereRadius - Radius of the sphere to cast (default: 0.3)
   * @param castDistance - Distance to cast sphere downward. If not provided, will use boundingBoxHeight/2 + 0.1 if boundingBoxHeight is provided (default: 0.5)
   * @param castOriginOffset - Offset from body center to start cast (default: 0)
   * @param boundingBoxHeight - Optional bounding box height. If provided and castDistance is not provided, will calculate castDistance as boundingBoxHeight/2 + 0.1
   * @returns Ground check result with hit info, or null if body/world not found
   * 
   * @example
   * ```typescript
   * // Check if player is grounded using sphere cast (more stable than raycast)
   * const groundCheck = physics.checkGroundSphereCast('main', 'player', 0.3, 0.5, 0.8);
   * if (groundCheck && groundCheck.isGrounded) {
   *   console.log('Player is on ground!');
   * }
   * 
   * // Use bounding box height for automatic cast distance
   * const bboxHeight = 1.4;
   * const groundCheck = physics.checkGroundSphereCast('main', 'player', 0.3, undefined, 0, bboxHeight);
   * ```
   */
  checkGroundSphereCast(
    worldId: string,
    bodyId: string | CANNON.Body,
    sphereRadius: number = 0.3,
    castDistance?: number,
    castOriginOffset: number = 0,
    boundingBoxHeight?: number
  ): { isGrounded: boolean; distance: number; hitPoint: THREE.Vector3 | null; hitNormal: THREE.Vector3 | null } | null {
    // Get world
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return null;

    // Get body
    let body: CANNON.Body | null = null;
    if (typeof bodyId === 'string') {
      body = this.getBody(worldId, bodyId);
    } else {
      body = bodyId;
    }
    if (!body) return null;

    // Calculate cast distance from bounding box height if not provided
    const finalCastDistance = castDistance !== undefined
      ? castDistance
      : (boundingBoxHeight !== undefined ? boundingBoxHeight / 2 + 0.1 : 0.5);

    // Cast multiple rays in a sphere pattern for sphere cast simulation
    // Since Cannon.js doesn't have native sphere cast, we use multiple raycasts
    // Using 5 rays: Center + 4 cardinal directions
    const rayOffsets = [
      { x: 0, z: 0 },           // Center
      { x: sphereRadius, z: 0 }, // Right
      { x: -sphereRadius, z: 0 }, // Left
      { x: 0, z: sphereRadius }, // Forward
      { x: 0, z: -sphereRadius } // Back
    ];

    let closestHit: { distance: number; hitPoint: CANNON.Vec3; hitNormal: CANNON.Vec3 } | null = null;
    let closestDistance = Infinity;

    for (const offset of rayOffsets) {
      const from = new CANNON.Vec3(
        body.position.x + offset.x,
        body.position.y - castOriginOffset,
        body.position.z + offset.z
      );
      const to = new CANNON.Vec3(
        body.position.x + offset.x,
        body.position.y - castOriginOffset - finalCastDistance,
        body.position.z + offset.z
      );

      const result = new CANNON.RaycastResult();
      worldMeta.world.raycastClosest(from, to, {
        skipBackfaces: true,
        collisionFilterMask: -1,
      }, result);

      if (result.hasHit) {
        // Skip if hit the body we're checking
        if (result.body === body) {
          continue;
        }

        const distance = from.distanceTo(result.hitPointWorld);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestHit = {
            distance,
            hitPoint: result.hitPointWorld,
            hitNormal: result.hitNormalWorld
          };
        }
      }
    }

    if (closestHit) {
      return {
        isGrounded: true,
        distance: closestHit.distance,
        hitPoint: new THREE.Vector3(
          closestHit.hitPoint.x,
          closestHit.hitPoint.y,
          closestHit.hitPoint.z
        ),
        hitNormal: new THREE.Vector3(
          closestHit.hitNormal.x,
          closestHit.hitNormal.y,
          closestHit.hitNormal.z
        ),
      };
    }

    return {
      isGrounded: false,
      distance: finalCastDistance,
      hitPoint: null,
      hitNormal: null,
    };
  }

  /**
   * Check if an object/position is grounded using raycast (non-physics body)
   * Useful for checking ground without a physics body (e.g., visual-only objects)
   * 
   * @param worldId - World identifier
   * @param position - Position to check from (THREE.Vector3 or [x, y, z])
   * @param rayDistance - Distance to cast ray downward (default: 1.0)
   * @returns Ground check result, or null if world not found
   * 
   * @example
   * ```typescript
   * // Check if a Three.js mesh is grounded
   * const groundCheck = physics.checkGroundRaycastFromPosition(
   *   'main',
   *   playerMesh.position,
   *   1.5
   * );
   * if (groundCheck && groundCheck.isGrounded) {
   *   console.log('Position is grounded!');
   * }
   * ```
   */
  checkGroundRaycastFromPosition(
    worldId: string,
    position: THREE.Vector3 | [number, number, number],
    rayDistance: number = 1.0
  ): { isGrounded: boolean; distance: number; hitPoint: THREE.Vector3 | null; hitNormal: THREE.Vector3 | null } | null {
    // Get world
    const worldMeta = this.worldRegistry.get(worldId);
    if (!worldMeta) return null;

    // Convert position to CANNON.Vec3
    const pos = toVec3(position);

    // Create ray from position downward
    const from = new CANNON.Vec3(pos.x, pos.y, pos.z);
    const to = new CANNON.Vec3(pos.x, pos.y - rayDistance, pos.z);

    // Perform raycast
    const result = new CANNON.RaycastResult();
    worldMeta.world.raycastClosest(from, to, {}, result);

    // Check if hit
    if (result.hasHit) {
      return {
        isGrounded: true,
        distance: from.distanceTo(result.hitPointWorld),
        hitPoint: new THREE.Vector3(
          result.hitPointWorld.x,
          result.hitPointWorld.y,
          result.hitPointWorld.z
        ),
        hitNormal: new THREE.Vector3(
          result.hitNormalWorld.x,
          result.hitNormalWorld.y,
          result.hitNormalWorld.z
        ),
      };
    }

    return {
      isGrounded: false,
      distance: rayDistance,
      hitPoint: null,
      hitNormal: null,
    };
  }

  /**
   * Create a wireframe mesh for a physics body for debugging/visualization.
   * 
   * @param body - The CANNON.Body to create wireframe for
   * @param options - Wireframe options
   * @param options.color - Wireframe color (default: 0x00ff00)
   * @param options.opacity - Wireframe opacity (default: 0.6)
   * @param options.linewidth - Line width (default: 1)
   * @returns THREE.LineSegments wireframe mesh or null if shape not supported
   * 
   * @example
   * ```typescript
   * const body = physicsHandler.getBody('main-world', 'player');
   * const wireframe = physicsHandler.createWireframe(body);
   * scene.add(wireframe);
   * 
   * // Update wireframe position/rotation in animation loop
   * wireframe.position.set(body.position.x, body.position.y, body.position.z);
   * wireframe.quaternion.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w);
   * ```
   */
  createWireframe(
    body: CANNON.Body,
    options: {
      color?: number;
      opacity?: number;
      linewidth?: number;
    } = {}
  ): THREE.LineSegments | null {
    if (!body || body.shapes.length === 0) return null;

    const shape = body.shapes[0];
    let geometry: THREE.BufferGeometry | null = null;

    // Create geometry based on shape type
    if (shape instanceof CANNON.Box) {
      const halfExtents = shape.halfExtents;
      geometry = new THREE.BoxGeometry(
        halfExtents.x * 2,
        halfExtents.y * 2,
        halfExtents.z * 2
      );
    } else if (shape instanceof CANNON.Sphere) {
      geometry = new THREE.SphereGeometry(shape.radius, 16, 16);
    } else if (shape instanceof CANNON.Cylinder) {
      geometry = new THREE.CylinderGeometry(
        shape.radiusTop,
        shape.radiusBottom,
        shape.height,
        16
      );
    } else if (shape instanceof CANNON.Plane) {
      // For planes, create a large flat box
      geometry = new THREE.BoxGeometry(10, 0.1, 10);
    } else if (shape instanceof CANNON.ConvexPolyhedron) {
      // For convex polyhedrons, create wireframe from vertices
      const vertices: number[] = [];
      const indices: number[] = [];

      shape.vertices.forEach((v: CANNON.Vec3) => {
        vertices.push(v.x, v.y, v.z);
      });

      shape.faces.forEach((face: number[]) => {
        const faceIndices = face;
        for (let i = 0; i < faceIndices.length - 2; i++) {
          indices.push(faceIndices[0], faceIndices[i + 1], faceIndices[i + 2]);
        }
      });

      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
      geometry.setIndex(indices);
    }

    if (!geometry) return null;

    // Create wireframe
    const wireframeGeometry = new THREE.WireframeGeometry(geometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: options.color ?? 0x00ff00,
      transparent: true,
      opacity: options.opacity ?? 0.6,
      linewidth: options.linewidth ?? 1,
    });

    const wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);

    // Set initial position and rotation from body
    wireframe.position.set(body.position.x, body.position.y, body.position.z);
    wireframe.quaternion.set(
      body.quaternion.x,
      body.quaternion.y,
      body.quaternion.z,
      body.quaternion.w
    );

    return wireframe;
  }

  /**
   * Update wireframe position and rotation to match physics body.
   * Call this in your animation loop to keep wireframe in sync.
   * 
   * @param wireframe - The wireframe mesh to update
   * @param body - The physics body to sync with
   * 
   * @example
   * ```typescript
   * // In animation loop
   * physicsHandler.updateWireframe(wireframe, body);
   * ```
   */
  updateWireframe(
    wireframe: THREE.LineSegments,
    body: CANNON.Body
  ): void {
    wireframe.position.set(body.position.x, body.position.y, body.position.z);
    wireframe.quaternion.set(
      body.quaternion.x,
      body.quaternion.y,
      body.quaternion.z,
      body.quaternion.w
    );
  }
}

// Export a default singleton instance for backward compatibility
export const physicsHandler = new PhysicsHandler();
